import axios from "axios";

const SPONTE_API_URL = process.env.REACT_APP_SPONTE_API_URL;
const SPONTE_USERNAME = process.env.REACT_APP_SPONTE_USERNAME;
const SPONTE_PASSWORD = process.env.REACT_APP_SPONTE_PASSWORD;

const sponteApi = axios.create({
  baseURL: SPONTE_API_URL,
  timeout: 15000,
});

/**
 * Authenticates with the Sponte API and returns a bearer token.
 */
async function getSponteToken() {
  const response = await sponteApi.post("/login", {
    Username: SPONTE_USERNAME,
    Password: SPONTE_PASSWORD,
  });

  const data = response.data || {};
  const token = data.token || data.Token || response.data;

  if (!token || typeof token !== "string") {
    throw new Error("Sponte login did not return a valid token.");
  }

  return token;
}

/**
 * Fetches the Sponte student record by email.
 * When the API returns multiple records (e.g. after a unit transfer that
 * created a duplicate), picks the active one (ativo === true).
 * Falls back to the last record if none is explicitly marked active.
 */
async function getSponteUserByEmail(token, email) {
  var response = await sponteApi.get("/Aluno/ObterPorUserEmail", {
    params: { email: email },
    headers: { Authorization: "Bearer " + token },
  });

  var result = response.data;

  var student;
  if (Array.isArray(result)) {
    if (result.length === 0) {
      throw new Error("No Sponte student found for email: " + email);
    }
    // Prefer the record explicitly flagged as active.
    // After a unit transfer Sponte may create a duplicate; the old record
    // (first in the array) is typically inactive, causing books to disappear.
    var active = result.find(function (r) { return r.ativo === true; });
    student = active || result[result.length - 1];
  } else {
    student = result;
  }

  if (!student || !student.alunoId) {
    throw new Error(
      "No Sponte student found for email: " + email
    );
  }

  return student;
}

/**
 * High-level function: authenticates with Sponte, then looks up the student
 * by email and returns the alunoId.
 *
 * @param {string} email - The user's email (from IES login).
 * @returns {Promise<number>} The Sponte alunoId.
 */
export async function fetchSponteAlunoId(email) {
  const token = await getSponteToken();
  const student = await getSponteUserByEmail(token, email);

  if (!student.alunoId) {
    throw new Error(
      `Sponte student record for ${email} does not contain an alunoId.`
    );
  }

  return student.alunoId;
}

/**
 * Returns ALL Sponte alunoIds for the given email (active + inactive).
 * Used for progress fallback: after unit transfer or credential unification,
 * old progress may be stored under a previously active alunoId.
 *
 * @param {string} email - The user's email (from IES login).
 * @returns {Promise<number[]>} Array of all Sponte alunoIds found.
 */
export async function fetchAllSponteAlunoIds(email) {
  const token = await getSponteToken();
  const response = await sponteApi.get("/Aluno/ObterPorUserEmail", {
    params: { email: email },
    headers: { Authorization: "Bearer " + token },
  });

  const result = response.data;
  if (!result) return [];

  const records = Array.isArray(result) ? result : [result];
  return records.filter(function(r) { return r && r.alunoId; }).map(function(r) { return r.alunoId; });
}
